import React, { useCallback, useMemo, useState } from 'react';
import { Container, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { paths } from '../../../routes';
import CustomButton from '../../../components/CustomButton';
import CustomModal from '../../common/CustomModal';
import {
    GeneratedDate,
    RecurrencePattern,
    datesFromPreview,
    defaultRecurrencePattern,
    toServerPattern,
} from '../../../utils/recurrence';
import { previewSeries } from '../../../flows/seriesModuleFlow';
import { SeriesRequestPayload } from '../../../types/bookingCreation';
import { seriesRequestFrom } from './components/SeriesBooking/seriesRequest';
import Stepper from '../../../components/Stepper';
import BookingDataForm from './components/SeriesBooking/BookingDataForm';
import type { SeriesBookingData } from './components/SeriesBooking/BookingDataForm';
import RecurrencePatternForm from './components/SeriesBooking/RecurrencePatternForm';
import SeriesDateList from './components/SeriesBooking/SeriesDateList';
import BatchCreationProgress from './components/SeriesBooking/BatchCreationProgress';
import { EMPTY_BOOKING_ATTRIBUTION } from '../../../components/booking/BookingAttributionFields';

type WizardStep = 0 | 1 | 2 | 3;

const initialBookingData: SeriesBookingData = {
    assetId: '',
    groupId: '',
    assetName: '',
    locationId: '',
    locationName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    attribution: EMPTY_BOOKING_ATTRIBUTION,
    address: '',
    country: '',
    customerCity: '',
    zipCode: '',
    startTime: '',
    endTime: '',
    startDate: null,
    numberOfGuests: 0,
    eventType: '',
    comment: '',
    useCase: 'venue',
    city: '',
    additionalServices: [],
};

const CreateSeriesBookingPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const stepLabels = [
        t('series_booking.steps.booking_data'),
        t('series_booking.steps.recurrence_pattern'),
        t('series_booking.steps.date_review'),
        t('series_booking.steps.confirmation'),
    ];

    // Wizard state
    const [currentStep, setCurrentStep] = useState<WizardStep>(0);
    const [bookingData, setBookingData] = useState<SeriesBookingData>(initialBookingData);
    const [recurrencePattern, setRecurrencePattern] = useState<RecurrencePattern>(defaultRecurrencePattern);
    const [generatedDates, setGeneratedDates] = useState<GeneratedDate[]>([]);
    // The dates come from the server (series plan §4.3); while it answers the
    // step cannot advance, and a refusal shows its reason instead of an empty
    // list (a failure must not look like "no dates").
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);

    // Cancel confirmation dialog
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    // Track batch creation state to disable navigation
    const [creationState, setCreationState] = useState<'idle' | 'creating' | 'complete'>('idle');

    // What the series routes take: the booking fields and the pattern.
    const seriesRequest = useMemo<SeriesRequestPayload | null>(() => {
        if (!bookingData.startDate) return null;
        return seriesRequestFrom(
            {
                groupId: bookingData.groupId,
                startTime: bookingData.startTime,
                endTime: bookingData.endTime,
                customerDetails: {
                    firstName: bookingData.firstName,
                    lastName: bookingData.lastName,
                    email: bookingData.email,
                    phone: bookingData.phone,
                    address: bookingData.address,
                    country: bookingData.country,
                    city: bookingData.customerCity,
                    zipCode: bookingData.zipCode,
                    company: bookingData.company,
                },
                numberOfGuests: bookingData.numberOfGuests,
                eventType: bookingData.eventType,
                comment: bookingData.comment,
                additionalServices: bookingData.additionalServices,
                // #1810: every occurrence carries the same attribution.
                attribution: bookingData.attribution,
            },
            toServerPattern(recurrencePattern, bookingData.startDate),
        );
    }, [bookingData, recurrencePattern]);

    // Step navigation
    const handleNext = useCallback(async () => {
        if (currentStep === 0 && bookingData.startDate) {
            // Pre-select the weekday of the start date for weekly pattern
            const startWeekday = bookingData.startDate.day();
            if (recurrencePattern.type === 'weekly' && recurrencePattern.weekdays.length === 0) {
                setRecurrencePattern((prev) => ({
                    ...prev,
                    weekdays: [startWeekday],
                }));
            }
        }
        if (currentStep === 1 && seriesRequest) {
            // The server expands the pattern and checks every date.
            setIsPreviewing(true);
            setPreviewError(null);
            const preview = await previewSeries(seriesRequest);
            setIsPreviewing(false);
            if ('error' in preview) {
                setPreviewError(preview.error);
                return;
            }
            setGeneratedDates(datesFromPreview(preview.occurrences, bookingData.startTime, bookingData.endTime));
        }
        setCurrentStep((prev) => Math.min(prev + 1, 3) as WizardStep);
    }, [currentStep, bookingData, recurrencePattern, seriesRequest]);

    const handleBack = useCallback(() => {
        setCurrentStep((prev) => Math.max(prev - 1, 0) as WizardStep);
    }, []);

    const handleCancel = useCallback(() => {
        setShowCancelDialog(true);
    }, []);

    const handleConfirmCancel = useCallback(() => {
        navigate(paths.rentalBooking);
    }, [navigate]);

    // Step validation
    const isStepValid = useMemo(() => {
        switch (currentStep) {
            case 0:
                return !!(
                    bookingData.assetId &&
                    bookingData.startDate &&
                    bookingData.startTime &&
                    bookingData.endTime &&
                    bookingData.firstName &&
                    bookingData.lastName &&
                    bookingData.email &&
                    // #1749: the guest count carries a `*` and is the capacity
                    // field from #1750 — it filters the availability query, so a
                    // series created with 0 is the shape that bug was about. The
                    // single-booking venue form enforces it transitively (its
                    // venue fetch is gated on a truthy count); this form fetches
                    // on city alone, so it needs the check stated outright.
                    bookingData.numberOfGuests > 0
                );
            case 1:
                if (recurrencePattern.type === 'custom') {
                    return recurrencePattern.customDates.length > 0;
                }
                if (recurrencePattern.type === 'weekly') {
                    return recurrencePattern.weekdays.length > 0;
                }
                return true;
            case 2:
                return generatedDates.some((d) => d.included);
            default:
                return true;
        }
    }, [currentStep, bookingData, recurrencePattern, generatedDates]);

    const includedDatesCount = useMemo(
        () => generatedDates.filter((d) => d.included).length,
        [generatedDates],
    );

    return (
        <Container maxWidth={false} sx={{ py: 3, maxWidth: '1200px' }}>
            <Stack spacing={3}>
                {/* Header */}
                <div className={'flex items-center justify-between'}>
                    <Typography variant={'h5'} fontWeight={600}>
                        {t('series_booking.page_title')}
                    </Typography>
                    <CustomButton
                        title={t('series_booking.buttons.cancel')}
                        onClick={handleCancel}
                        isFilled={false}
                        disabled={creationState !== 'idle'}
                    />
                </div>

                {/* Stepper */}
                <Stepper
                    steps={stepLabels}
                    currentStep={currentStep}
                />

                {/* Step Content — all steps stay mounted to preserve state */}
                <div className={'min-h-[400px]'}>
                    <div style={{ display: currentStep === 0 ? 'block' : 'none' }}>
                        <BookingDataForm
                            data={bookingData}
                            onChange={setBookingData}
                        />
                    </div>

                    <div style={{ display: currentStep === 1 ? 'block' : 'none' }}>
                        <RecurrencePatternForm
                            pattern={recurrencePattern}
                            onChange={setRecurrencePattern}
                        />
                        {previewError && (
                            <p className={'mt-3 text-sm text-foreground'}>
                                {t('series_booking.date_review.preview_failed', { reason: previewError })}
                            </p>
                        )}
                    </div>

                    {currentStep >= 2 && (
                        <div style={{ display: currentStep === 2 ? 'block' : 'none' }}>
                            <SeriesDateList
                                dates={generatedDates}
                                onDatesChange={setGeneratedDates}
                            />
                        </div>
                    )}

                    {currentStep >= 3 && seriesRequest && (
                        <div style={{ display: currentStep === 3 ? 'block' : 'none' }}>
                            <BatchCreationProgress
                                dates={generatedDates.filter((d) => d.included)}
                                excludedDates={generatedDates.filter((d) => !d.included)}
                                bookingData={bookingData}
                                seriesRequest={seriesRequest}
                                onCreationStateChange={setCreationState}
                            />
                        </div>
                    )}
                </div>

                {/* Navigation (Steps 0–2) */}
                {currentStep < 3 && (
                    <div className={'flex justify-between border-t border-gray-200 pt-4'}>
                        <CustomButton
                            title={t('series_booking.buttons.back')}
                            onClick={handleBack}
                            isFilled={false}
                            disabled={currentStep === 0}
                        />
                        <CustomButton
                            title={
                                currentStep === 2
                                    ? t('series_booking.buttons.book_available', { count: includedDatesCount })
                                    : t('series_booking.buttons.next')
                            }
                            onClick={handleNext}
                            disabled={!isStepValid || isPreviewing}
                        />
                    </div>
                )}

                {/* Step 3: Back button only — disabled once creation starts */}
                {currentStep === 3 && creationState === 'idle' && (
                    <div className={'flex border-t border-gray-200 pt-4'}>
                        <CustomButton
                            title={t('series_booking.buttons.back')}
                            onClick={handleBack}
                            isFilled={false}
                        />
                    </div>
                )}
            </Stack>

            {/* Cancel confirmation dialog */}
            <CustomModal
                open={showCancelDialog}
                onClose={() => setShowCancelDialog(false)}
                title={t('series_booking.cancel_dialog.title')}
                description={t('series_booking.cancel_dialog.description')}
                saveButton={
                    <CustomButton
                        title={t('series_booking.buttons.yes_cancel')}
                        onClick={handleConfirmCancel}
                        variant={'delete'}
                    />
                }
                closeButton={
                    <CustomButton
                        title={t('series_booking.buttons.continue_editing')}
                        onClick={() => setShowCancelDialog(false)}
                        isFilled={false}
                    />
                }
            />
        </Container>
    );
};

export default CreateSeriesBookingPage;
